import { useState, useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { BarChart3, TrendingUp } from 'lucide-react';
import clsx from 'clsx';
import { Card } from '@components/ui';

// Chart.js kayıt
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

/**
 * FinancialChart - Chart.js ile gelir/gider trend grafiği
 * Sayfada inline Card olarak gösterilir
 */
export default function FinancialChart({ monthlyData = [], formatCurrency }) {
    const [chartType, setChartType] = useState('bar'); // bar, line

    // Veriyi hazırla
    const chartData = useMemo(() => {
        if (!monthlyData || monthlyData.length === 0) return { labels: [], incomes: [], expenses: [], nets: [] };

        return {
            labels: monthlyData.map(m => m.monthLabel || m.month),
            incomes: monthlyData.map(m => m.income || 0),
            expenses: monthlyData.map(m => m.expense || 0),
            nets: monthlyData.map(m => (m.income || 0) - (m.expense || 0)),
        };
    }, [monthlyData]);

    // Chart.js bar data
    const data = {
        labels: chartData.labels,
        datasets: [
            {
                label: 'Gelir',
                data: chartData.incomes,
                backgroundColor: 'rgba(16, 185, 129, 0.8)',
                borderColor: 'rgb(16, 185, 129)',
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            },
            {
                label: 'Gider',
                data: chartData.expenses,
                backgroundColor: 'rgba(244, 63, 94, 0.8)',
                borderColor: 'rgb(244, 63, 94)',
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
            },
        ],
    };

    // Chart.js options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 16,
                    font: { size: 12, weight: '500' },
                },
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                titleFont: { size: 13, weight: 'bold' },
                bodyFont: { size: 12 },
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        const formatted = formatCurrency ? formatCurrency(value) : `₺${value.toLocaleString('tr-TR')}`;
                        return `${context.dataset.label}: ${formatted}`;
                    },
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    font: { size: 11 },
                    maxRotation: 45,
                    minRotation: 0,
                },
            },
            y: {
                grid: { color: 'rgba(0, 0, 0, 0.05)' },
                ticks: {
                    font: { size: 11 },
                    callback: (value) => {
                        if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                        return value;
                    },
                },
            },
        },
    };

    // Line chart dataset
    const lineData = {
        labels: chartData.labels,
        datasets: [
            {
                label: 'Gelir',
                data: chartData.incomes,
                borderColor: 'rgb(16, 185, 129)',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                borderWidth: 2.5,
                tension: 0.4,
                fill: true,
                pointRadius: 4,
                pointHoverRadius: 6,
            },
            {
                label: 'Gider',
                data: chartData.expenses,
                borderColor: 'rgb(244, 63, 94)',
                backgroundColor: 'rgba(244, 63, 94, 0.1)',
                borderWidth: 2.5,
                tension: 0.4,
                fill: true,
                pointRadius: 4,
                pointHoverRadius: 6,
            },
        ],
    };

    // Toplam hesaplamalar
    const totalIncome = chartData.incomes.reduce((sum, val) => sum + val, 0);
    const totalExpense = chartData.expenses.reduce((sum, val) => sum + val, 0);
    const netBalance = totalIncome - totalExpense;

    if (!monthlyData || monthlyData.length === 0) {
        return null;
    }

    return (
        <Card padding="lg">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-text-primary flex items-center gap-2">
                    <TrendingUp size={20} className="text-primary" />
                    Gelir / Gider Trendi
                </h3>

                {/* Chart Type Toggle */}
                <div className="flex items-center gap-1 bg-bg-tertiary rounded-lg p-1">
                    <button
                        onClick={() => setChartType('bar')}
                        className={clsx(
                            "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                            chartType === 'bar'
                                ? "bg-bg-secondary text-text-primary shadow-sm"
                                : "text-text-muted hover:text-text-secondary"
                        )}
                    >
                        <BarChart3 size={14} className="inline mr-1" />
                        Çubuk
                    </button>
                    <button
                        onClick={() => setChartType('line')}
                        className={clsx(
                            "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                            chartType === 'line'
                                ? "bg-bg-secondary text-text-primary shadow-sm"
                                : "text-text-muted hover:text-text-secondary"
                        )}
                    >
                        <TrendingUp size={14} className="inline mr-1" />
                        Çizgi
                    </button>
                </div>
            </div>

            {/* Chart */}
            <div className="h-64 sm:h-72 md:h-80">
                {chartType === 'bar' ? (
                    <Bar data={data} options={options} />
                ) : (
                    <Line data={lineData} options={options} />
                )}
            </div>

            {/* Summary Footer */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border-light">
                <div className="text-center">
                    <p className="text-xs text-text-muted mb-0.5">Toplam Gelir</p>
                    <p className="text-base sm:text-lg font-bold text-success">
                        {formatCurrency ? formatCurrency(totalIncome) : `₺${totalIncome.toLocaleString('tr-TR')}`}
                    </p>
                </div>
                <div className="text-center">
                    <p className="text-xs text-text-muted mb-0.5">Toplam Gider</p>
                    <p className="text-base sm:text-lg font-bold text-error">
                        {formatCurrency ? formatCurrency(totalExpense) : `₺${totalExpense.toLocaleString('tr-TR')}`}
                    </p>
                </div>
                <div className="text-center">
                    <p className="text-xs text-text-muted mb-0.5">Net Bakiye</p>
                    <p className={clsx("text-base sm:text-lg font-bold", netBalance >= 0 ? 'text-success' : 'text-error')}>
                        {formatCurrency ? formatCurrency(netBalance) : `₺${netBalance.toLocaleString('tr-TR')}`}
                    </p>
                </div>
            </div>
        </Card>
    );
}
